/**
 * Pure builders that turn a `(AppState, BoatTelemetry, ManeuverWindow)`
 * tuple into the read-only views consumed by:
 *   - the {@link evaluateAutoCheck} checklist interpreter,
 *   - the {@link computeFinalScore} scoring engine.
 *
 * Lives outside `maneuver.store` so it can be unit-tested without Angular
 * (the store layers on signals, RxJS-style sampling, and lifecycle).
 *
 * The "window" is the live aggregation of in-flight observations during a
 * maneuver: drag duration, latest contact speed, etc. The store owns this
 * struct and feeds it back into the context every tick.
 */

import type {
  AnchorDeploymentState,
  AnchorHoldingState,
  AppState,
  BoatTelemetry,
} from "../../../simulation-core";
import type { ChecklistContext } from "./command-checklist";

/**
 * Coarse anchor state used by the checklist. Maps the sim's two separate
 * enums (`AnchorDeploymentState` for the GLB/winch and
 * `AnchorHoldingState` for seabed grip) onto one unified label.
 *
 * Priority: holding-state info wins when the anchor is in the water, so a
 * `setting / dragging / holding` outcome is faithfully reported; otherwise
 * the deployment phase (`up / dropping / down / weighing`) speaks.
 */
export function deriveAnchorState(
  deployment: AnchorDeploymentState,
  holding: AnchorHoldingState,
): ChecklistContext["anchorState"] {
  if (deployment === "up") return "up";
  if (deployment === "dropping") return "dropping";
  if (deployment === "weighing") return "weighing";
  // deployment === "down"
  switch (holding) {
    case "holding":
      return "holding";
    case "dragging":
      return "dragging";
    case "setting":
    case "stowed":
      return "down";
  }
}

/**
 * In-flight observations accumulated by the maneuver store across the run.
 * Reset to zero at `start()`; updated each tick from telemetry + impact
 * events.
 */
export interface ManeuverWindow {
  /** Seconds the anchor has spent in `dragging` state during this run. */
  readonly anchorDragSec: number;
  /** Seconds since the anchor last LEFT the `dragging` state. */
  readonly secondsSinceLastDrag: number;
  /** Worst contact speed observed during the run (m/s). */
  readonly maxContactSpeedMs: number;
  /** Distinct cleats whose tension ratio exceeded the threshold this run. */
  readonly overTensionedLines: ReadonlySet<string>;
  /** Whether the player accepted any crew assistance during the run. */
  readonly crewAssisted: boolean;
  /** Whether any collision happened during the run (any kind, any speed). */
  readonly hadCollision: boolean;
  /** Number of buoy contacts observed (counted regardless of penalty). */
  readonly buoyContactCount: number;
  /** Whether the anchor was ever in `dragging` state during the run. */
  readonly hadDrag: boolean;
  /** Absolute cumulative heading change since start, degrees (no wrap). */
  readonly cumulativeTurnDeg: number;
  /** Last observed heading in radians; `null` until the first tick. */
  readonly lastHeadingRad: number | null;
  /**
   * Number of gear shifts (throttle sign flips) observed while the
   * pre-shift throttle magnitude was above the high-RPM threshold.
   * Detected from telemetry rather than UI events so any input source
   * (keyboard, UI lever, AI) counts.
   */
  readonly highRpmGearShifts: number;
  /** Last observed signed throttle in [-1, 1]; `null` until the first tick. */
  readonly lastThrottle: number | null;
}

export const EMPTY_WINDOW: ManeuverWindow = {
  anchorDragSec: 0,
  secondsSinceLastDrag: Number.POSITIVE_INFINITY,
  maxContactSpeedMs: 0,
  overTensionedLines: new Set<string>(),
  crewAssisted: false,
  hadCollision: false,
  buoyContactCount: 0,
  hadDrag: false,
  cumulativeTurnDeg: 0,
  lastHeadingRad: null,
  highRpmGearShifts: 0,
  lastThrottle: null,
};

export interface TargetGeometry {
  readonly worldX: number;
  readonly worldZ: number;
}

/**
 * Compute the heading deviation from the wind direction in [0, 180] degrees.
 *
 * `state.windDirectionDeg` is meteorological (where the wind comes FROM),
 * so we compare the boat's heading (where the bow points TO) against the
 * upwind direction. A bow-into-wind boat returns ~0; a downwind boat ~180.
 */
export function headingVsWindDeg(
  headingRad: number,
  windFromDeg: number,
): number {
  const headingDeg = ((headingRad * 180) / Math.PI + 360) % 360;
  let diff = Math.abs(headingDeg - windFromDeg);
  if (diff > 180) diff = 360 - diff;
  return diff;
}

/**
 * Horizontal distance between the player boat and the maneuver target.
 * Returns `Infinity` when no target is set (e.g. before `declare()`).
 */
export function distanceFromTarget(
  boatPos: { readonly x: number; readonly z: number },
  target: TargetGeometry | null,
): number {
  if (target === null) return Number.POSITIVE_INFINITY;
  const dx = boatPos.x - target.worldX;
  const dz = boatPos.z - target.worldZ;
  return Math.hypot(dx, dz);
}

/**
 * Anchor scope ratio (rode length / depth at the drop point). Returns
 * `NaN` when no anchor is deployed (no rode paid out). Bounded below by
 * 0 to keep the predicate sensible if the sim ever reports a negative
 * depth.
 */
export function anchorScopeRatio(state: AppState): number {
  if (state.anchorState === "up") return Number.NaN;
  const depth = Math.max(0.1, state.anchorEstimatedDepth);
  return state.anchorChainLength / depth;
}

/**
 * Count of deployed fenders per side. `state.fenderDeployed` is keyed by
 * position (`bow_port`, `midship_starboard`, …); we just sum suffixes.
 */
export function countFenders(
  fenderDeployed: Readonly<Record<string, boolean>>,
): { port: number; starboard: number } {
  let port = 0;
  let starboard = 0;
  for (const [key, value] of Object.entries(fenderDeployed)) {
    if (!value) continue;
    if (key.endsWith("_port")) port++;
    else if (key.endsWith("_starboard")) starboard++;
  }
  return { port, starboard };
}

/**
 * Map a snapshot of mooring targets onto `{cleat → attached|stowed|free}`.
 * `null` in `mooringTargets` is treated as `stowed` (idle line); any
 * non-null value means the line is attached.
 *
 * Distinguishing `free` vs `stowed` requires crew-store insight (a line
 * being actively coiled vs put away); for the checklist's purposes both
 * count as "not in use", so we collapse them to `stowed`.
 */
export function mapMooringLines(
  mooringTargets: Readonly<Record<string, string | null>>,
): Record<string, "attached" | "stowed" | "free"> {
  const out: Record<string, "attached" | "stowed" | "free"> = {};
  for (const [cleat, target] of Object.entries(mooringTargets)) {
    out[cleat] = target === null ? "stowed" : "attached";
  }
  return out;
}

/**
 * Build a {@link ChecklistContext} suitable for `evaluateAutoCheck`.
 *
 * The store calls this each tick with the latest `state`, `telemetry`, and
 * its in-flight {@link ManeuverWindow}.
 */
export function buildChecklistContext(input: {
  readonly state: AppState;
  readonly telemetry: BoatTelemetry;
  readonly window: ManeuverWindow;
  readonly target: TargetGeometry | null;
  readonly crewRoles?: Readonly<Record<string, string>>;
}): ChecklistContext {
  const { state, telemetry, window, target } = input;
  return {
    anchorState: deriveAnchorState(state.anchorState, state.anchorHoldingState),
    mooringLines: mapMooringLines(state.mooringTargets),
    speedMs: telemetry.sog,
    headingVsWindDeg: headingVsWindDeg(
      telemetry.heading,
      state.windDirectionDeg,
    ),
    anchorScopeRatio: anchorScopeRatio(state),
    secondsSinceLastDrag: window.secondsSinceLastDrag,
    fendersDeployed: countFenders(state.fenderDeployed),
    distanceFromTargetM: distanceFromTarget(
      { x: telemetry.posX, z: telemetry.posZ },
      target,
    ),
    crew: input.crewRoles ?? {},
    cumulativeTurnDeg: window.cumulativeTurnDeg,
  };
}
